import type TableViewConversationRelation from "../store/schema/TableViewConversationRelation";
import type { ClientItem } from "owa-mail-store";
import { owaDate, userDate, getISOString, getTimestamp } from "owa-datetime";
import type { OwaDate } from "owa-datetime";

// September 1st 4500 (Month starts from 0)
// Lazy initialization to avoid calling owaDate at module scope
let utcPinnedDateCache: OwaDate | null = null;
function getUtcPinnedDate(): OwaDate {
    if (!utcPinnedDateCache) {
        utcPinnedDateCache = owaDate("UTC", 4500, 8, 1);
    }
    return utcPinnedDateCache;
}

// For sending out the service request, we must use the UTC time when marking the conversation as pinned.
// Drop the 'Z' at the end of the timestamp
// This time is used when sending request to the server to mark items as pinned and we have to send the
// time in EWS format
let utcPinnedEwsTimeStampCache: string | null = null;
export function getUtcPinnedEwsTimeStamp(): string {
    if (!utcPinnedEwsTimeStampCache) {
        utcPinnedEwsTimeStampCache = getISOString(getUtcPinnedDate()).slice(0, -1);
    }
    return utcPinnedEwsTimeStampCache;
}

/**
 * Gets a boolean which indicates whether a table conversation relation is pinned
 * @param tableConversationRelation which contains the renew time stamp
 * @returns boolean which indicates whether the relation is pinned
 */
export function getIsTableConversationRelationPinned(tableConversationRelation: TableViewConversationRelation): boolean {
    return getIsPinnedTimestamp(tableConversationRelation.lastDeliveryOrRenewTimeStamp);
}

/**
 * Gets a boolean which indicates whether a table conversation relation is pinned
 * @param item which contains the renew time stamp
 * @returns boolean which indicates whether the item is pinned
 */
export function getIsTableItemDataPinned(item: ClientItem): boolean {
    return item.ReceivedOrRenewTime ? getIsPinnedTimestamp(item.ReceivedOrRenewTime) : false;
}

/**
 * Get if a time stamp is a pinned time stamp
 * @param renewTimestamp
 * @returns a flag indicating whether the given timestamp is a pinned timestamp
 */
export function getIsPinnedTimestamp(renewTimestamp: string): boolean {
    if (renewTimestamp === getLocalPinnedTimeStamp()) {
        return true;
    }
    
    const renewDate = new Date(renewTimestamp);
    
    if (renewDate.getTime() == getTimestamp(getUtcPinnedDate())) {
        return true;
    }
    
    // This looser fallback is to handle the case where the pinned timestamp was
    // stamped incorrectly by the client (likely using local time instead of UTC
    // time) where the timestamp may be off by a few hours. This should be safe
    // until the year 4500 and we can consider other solutions at that time.
    return renewDate.getUTCFullYear() === 4500;
}

// For updating client upon pinning,
// we need to convert the utc pinned time to local time to compare,
// as server always returns the local time.
function getLocalPinnedTimeStamp(): string {
    try {
        // If browser does not have the timezone set, toLocaleString will throw error
        // in which case we shall not do instant local update and have it update through notification
        new Date(getTimestamp(getUtcPinnedDate())).toLocaleString();
        
        return userDate(getUtcPinnedDate()).toString();
    }
    catch {
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2322 (71,9): Type 'null' is not assignable to type 'string'.
        // @ts-expect-error
        return null;
    }
}

export { getLocalPinnedTimeStamp };
